import "@mui/x-data-grid/themeAugmentation";

import { type Components, type Theme } from "@mui/material/styles";

import { colors } from "../colors.ts";

export const dataGrid: Components<Theme>["MuiDataGrid"] = {
  styleOverrides: {
    columnHeader: ({ theme }) => ({
      backgroundColor: theme.palette.primary._states.focus,
      display: "flex",
      alignItems: "center",
      fontSize: "12px",
      "&:focus": {
        outline: "none !important",
      },
      "&:focus-within": {
        outline: "none !important",
      },
      "& .MuiDataGrid-columnHeaderDraggableContainer": {
        flexDirection: "row",
        cursor: "grab",
        "&:active": {
          cursor: "grabbing",
        },
      },
      "& .MuiDataGrid-columnHeaderTitleContainer": {
        flexDirection: "row",
      },
      "&.MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer":
        {
          justifyContent: "flex-end",
        },
      "&.no-sort-icon .MuiDataGrid-sortIcon, &.no-sort-icon .MuiDataGrid-iconButtonContainer":
        {
          display: "none",
        },
      "& .MuiDataGrid-iconButtonContainer": {
        opacity: 0,
        transition: "opacity 0.15s ease",
      },
      "&:hover .MuiDataGrid-iconButtonContainer, &.MuiDataGrid-columnHeader--sorted .MuiDataGrid-iconButtonContainer":
        {
          opacity: 1,
        },
      "& .MuiDataGrid-iconButtonContainer .MuiIconButton-root": {
        backgroundColor: "transparent",
        boxShadow: "none",
        "&:hover": {
          backgroundColor: "transparent",
        },
      },
    }),
    "cell--textRight": {
      "& .MuiInputBase-input": {
        textAlign: "right",
      },
    },
    "cell--textCenter": {
      "& .MuiInputBase-input": {
        textAlign: "center",
      },
    },
    cell: ({ theme }) => ({
      borderColor: theme.palette.gray?.[300],
      display: "flex",
      alignItems: "center",
      boxShadow: "none",
      "&.MuiDataGrid-cell--editable": {
        backgroundColor: "transparent",
        "& .MuiInputBase-root": {
          height: "100%",
          boxShadow: "none",
          backgroundColor: "transparent",
          "& .MuiInputBase-input": {
            backgroundColor: "transparent",
            padding: "0 10px",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
        },
      },
      "& .MuiAutocomplete-root": {
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        "& .MuiOutlinedInput-root": {
          padding: "0 4px",
          gap: "4px",
          flexWrap: "nowrap",
          overflow: "hidden",
          alignItems: "center",
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
        },
        "& .MuiChip-root": {
          height: 22,
          margin: 0,
          fontSize: "12px",
        },
      },
      "&.MuiDataGrid-cell--editing:focus-within": {
        outline: "none !important",
      },
      "&:focus": {
        outline: "none !important",
      },
      "&:focus-within": {
        outline: "none !important",
      },
      "&.MuiDataGrid-cell--pinnedRight, &.MuiDataGrid-cell--pinnedLeft": {
        backgroundColor: theme.palette.background.default,
      },
      "&.Mui-error": {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        outline: `1px solid ${theme.palette.error.main}`,
        "& .MuiInputBase-root": {
          boxShadow: "none",
          backgroundColor: "transparent",
          color: theme.palette.error.contrastText,
          "& .MuiInputBase-input": {
            backgroundColor: "transparent",
            color: theme.palette.error.contrastText,
            padding: "0 10px",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
        },
      },
    }),
    row: ({ theme }) => ({
      "&:first-of-type .MuiDataGrid-cell": {
        borderColor: "transparent",
      },
      "&.MuiDataGrid-row--editing": {
        backgroundColor: theme.palette.action.hover,
        boxShadow: "none",
        "& .MuiDataGrid-cell": {
          backgroundColor: "transparent",
          boxShadow: "none",
          "&.MuiDataGrid-cell--withActions": {
            backgroundColor: "transparent",
          },
          "&.MuiDataGrid-cell--pinnedRight, &.MuiDataGrid-cell--pinnedLeft": {
            backgroundColor: theme.palette.action.hover,
          },
        },
      },
    }),
    "cell--withLeftBorder": {
      borderLeftStyle: "none",
    },
    "columnHeader--withLeftBorder": {
      borderLeftStyle: "none",
    },
    root: ({ theme }) => ({
      "& .MuiDataGrid-filler": {
        display: "none",
      },
      "&.has-editing-row .MuiDataGrid-row:not(.MuiDataGrid-row--editing)": {
        opacity: theme.palette.action.disabledOpacity,
      },
      "& .MuiDataGrid-columnHeaders:hover .MuiDataGrid-columnSeparator--resizable":
        {
          opacity: 1,
        },
      "&.editable-grid .MuiDataGrid-columnHeaderDraggableContainer": {
        cursor: "default",
        "&:active": { cursor: "default" },
      },
      "&.editable-grid .MuiDataGrid-columnSeparator": {
        display: "none !important",
      },
      "&.editable-grid .MuiDataGrid-columnHeaders:hover .MuiDataGrid-columnSeparator--resizable":
        {
          opacity: 0,
        },
    }),
    columnSeparator: ({ theme }) => ({
      display: "none",
      color: theme.palette.common.white,
      opacity: 0,
      transition: "opacity 0.15s ease",
      "&.MuiDataGrid-columnSeparator--resizable": {
        display: "flex",
      },
      "&.MuiDataGrid-columnSeparator--resizing": {
        opacity: 1,
      },
    }),
    virtualScroller: ({ theme }) => ({
      overflowX: "auto",
      overflowY: "hidden",
      "&::-webkit-scrollbar": {
        width: 8,
        height: 8,
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: theme.palette.grey[100],
        borderRadius: 4,
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.grey[400],
        borderRadius: 4,
        "&:hover": {
          backgroundColor: theme.palette.grey[500],
        },
      },
      scrollbarWidth: "thin",
      scrollbarColor: `${theme.palette.grey[400]} ${theme.palette.grey[100]}`,
    }),
    virtualScrollerContent: ({ theme }) => ({
      position: "relative",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        bottom: 0,
        width: 40,
        pointerEvents: "none",
        background: `linear-gradient(to right, ${theme.palette.mode === "dark" ? colors.blackAlpha[30] : colors.blackAlpha[10]}, transparent)`,
        opacity: "var(--datagrid-left-shadow-opacity, 0)",
        transition: "opacity 0.2s ease",
        zIndex: 2,
      },
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        width: 40,
        pointerEvents: "none",
        background: `linear-gradient(to left, ${theme.palette.mode === "dark" ? colors.blackAlpha[30] : colors.blackAlpha[10]}, transparent)`,
        opacity: "var(--datagrid-right-shadow-opacity, 0)",
        transition: "opacity 0.2s ease",
        zIndex: 2,
      },
    }),
    footerContainer: ({ theme }) => ({
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
      justifyContent: "space-between",
      alignItems: "center",
    }),
  },
};
